"use client"
import * as React from 'react'
import { useRouter } from 'next/navigation'
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, type ColumnDef, type SortingState, type Column as TableColumn, type VisibilityState, type RowSelectionState } from '@tanstack/react-table'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Loader2, SlidersHorizontal, MoreHorizontal, Circle } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../primitives/table'
import { Button } from '../primitives/button'
import { Checkbox } from '../primitives/checkbox'
import { Spinner } from '../primitives/spinner'
import { TooltipProvider } from '../primitives/tooltip'
import { TruncatedCell } from './TruncatedCell'
import { FilterBar, type FilterDef, type FilterValues } from './FilterBar'
import { useCustomFieldFilterDefs } from './utils/customFieldFilters'
import { fetchCustomFieldDefinitionsPayload, type CustomFieldsetDto } from './utils/customFieldDefs'
import { RowActions, type RowActionItem } from './RowActions'
import { subscribeOrganizationScopeChanged, type OrganizationScopeChangedDetail } from '@open-mercato/shared/lib/frontend/organizationEvents'
import { InjectionSpot } from './injection/InjectionSpot'
import { useInjectionDataWidgets } from './injection/useInjectionDataWidgets'
import { resolveInjectedIcon } from './injection/resolveInjectedIcon'
import { apiCall } from './utils/apiCall'
import { raiseCrudError } from './utils/serverErrors'
import { PerspectiveSidebar } from './PerspectiveSidebar'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { flash } from './FlashMessages'
import { useConfirmDialog } from './confirm-dialog'
import type {
  PerspectiveDto,
  RolePerspectiveDto,
  PerspectivesIndexResponse,
  PerspectiveSettings,
  PerspectiveSaveResponse,
} from '@open-mercato/shared/modules/perspectives/types'
import type {
  InjectionColumnDefinition,
  InjectionBulkActionDefinition,
  InjectionFilterDefinition,
  InjectionRowActionDefinition,
} from '@open-mercato/shared/modules/widgets/injection'
import { ComponentReplacementHandles } from '@open-mercato/shared/modules/widgets/component-registry'
import { insertByInjectionPlacement } from '@open-mercato/shared/modules/widgets/injection-position'
import {
  ExportMenu,
  resolveExportSections,
} from './data-table/DataTableExport'
import type { DataTableExportFormat, DataTableExportSectionConfig, DataTableExportConfig, ResolvedExportSection } from './data-table/DataTableExport'
export type { DataTableExportFormat, DataTableExportSectionConfig, DataTableExportConfig, ResolvedExportSection }
import {
  readPerspectiveCookie,
  writePerspectiveCookie,
  readPerspectiveSnapshot,
  writePerspectiveSnapshot,
  sanitizePerspectiveSettings,
  formatDurationLabel,
  PERSPECTIVE_COOKIE_PREFIX,
  PERSPECTIVE_STORAGE_PREFIX,
} from './data-table/perspectiveStorage'
import type { PerspectiveSnapshot } from './data-table/perspectiveStorage'
import {
  withDataTableNamespaces,
  resolveDefaultRowAction,
  pickDefaultRowAction,
  getColumnTruncateConfig,
  readInjectedColumnValue,
  shouldSkipTruncation,
  normalizeLabel,
  collectUniqueById,
  DEFAULT_ROW_CLICK_ACTION_IDS,
} from './data-table/dataTableHelpers'
export { withDataTableNamespaces } from './data-table/dataTableHelpers'

let refreshScheduled = false

function scheduleRouterRefresh(router: ReturnType<typeof useRouter>) {
  if (refreshScheduled) return
  refreshScheduled = true
  if (typeof window === 'undefined') {
    refreshScheduled = false
    return
  }
  window.requestAnimationFrame(() => {
    refreshScheduled = false
    try { router.refresh() } catch {}
  })
}

export type PaginationProps = {
  page: number
  pageSize: number
  total: number
  totalPages: number
  onPageChange: (page: number) => void
  durationMs?: number | null
  cacheStatus?: 'hit' | 'miss' | null
}

export type DataTableRefreshButton = {
  onRefresh: () => void
  label: string
  isRefreshing?: boolean
  disabled?: boolean
}

export type DataTablePerspectiveConfig = {
  tableId: string
  initialState?: {
    response?: PerspectivesIndexResponse
    activePerspectiveId?: string | null
    initialSettings?: PerspectiveSettings | null
  }
}

export type DataTableProps<T> = {
  columns: ColumnDef<T, any>[]
  data: T[]
  toolbar?: React.ReactNode
  title?: React.ReactNode
  actions?: React.ReactNode
  refreshButton?: DataTableRefreshButton
  sortable?: boolean
  sorting?: SortingState
  onSortingChange?: (s: SortingState) => void
  pagination?: PaginationProps
  isLoading?: boolean
  emptyState?: React.ReactNode
  error?: React.ReactNode | string | null
  // Optional per-row actions renderer. When provided, an extra trailing column is rendered.
  rowActions?: (row: T) => React.ReactNode
  // Optional row click handler. When provided, rows become clickable and show pointer cursor.
  // If not provided, DataTable will execute the first row action whose id matches rowClickActionIds.
  onRowClick?: (row: T) => void
  // Preferred action ids for default row clicks (applies when onRowClick is not set).
  // Defaults to ['edit', 'open'].
  rowClickActionIds?: string[]
  // Disable row click navigation when rowActions are present.
  disableRowClick?: boolean

  // Auto FilterBar options (rendered as toolbar when provided and no custom toolbar passed)
  searchValue?: string
  onSearchChange?: (v: string) => void
  searchPlaceholder?: string
  searchAlign?: 'left' | 'right'
  filters?: FilterDef[]
  filterValues?: FilterValues
  onFiltersApply?: (values: FilterValues) => void
  onFiltersClear?: () => void
  // When provided, DataTable will fetch custom field definitions and append filter controls for filterable ones.
  entityId?: string
  entityIds?: string[]
  exporter?: DataTableExportConfig | false
  perspective?: DataTablePerspectiveConfig
  embedded?: boolean
  onCustomFieldFilterFieldsetChange?: (fieldset: string | null, entityId?: string) => void
  customFieldFilterKeyExtras?: Array<string | number | boolean | null | undefined>
  injectionSpotId?: string
  injectionContext?: Record<string, unknown>
  replacementHandle?: string
}

const EMPTY_FILTER_DEFS: FilterDef[] = []
const EMPTY_FILTER_VALUES: FilterValues = Object.freeze({}) as FilterValues

type BulkActionExecuteResult = {
  ok: boolean
  message?: string
  affectedCount?: number
  progressJobId?: string | null
}

export function DataTable<T>({
  columns,
  data,
  toolbar,
  title,
  actions,
  refreshButton,
  sortable,
  sorting: sortingProp,
  onSortingChange,
  pagination,
  isLoading,
  emptyState,
  error,
  rowActions,
  onRowClick,
  rowClickActionIds,
  disableRowClick = false,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchAlign = 'right',
  filters: baseFilters = EMPTY_FILTER_DEFS,
  filterValues = EMPTY_FILTER_VALUES,
  onFiltersApply,
  onFiltersClear,
  entityId,
  entityIds,
  exporter,
  perspective,
  embedded = false,
  onCustomFieldFilterFieldsetChange,
  customFieldFilterKeyExtras,
  injectionSpotId,
  injectionContext,
  replacementHandle,
}: DataTableProps<T>) {
  const t = useT()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const router = useRouter()
  const resolvedRowClickActionIds = rowClickActionIds ?? DEFAULT_ROW_CLICK_ACTION_IDS
  const containerRef = React.useRef<HTMLDivElement>(null)
  const lastScopeRef = React.useRef<OrganizationScopeChangedDetail | null>(null)
  const hasInitializedScopeRef = React.useRef(false)
  React.useEffect(() => {
    return subscribeOrganizationScopeChanged((detail) => {
      const prev = lastScopeRef.current
      lastScopeRef.current = detail
      if (!hasInitializedScopeRef.current) {
        hasInitializedScopeRef.current = true
        return
      }
      if (
        prev &&
        prev.organizationId === detail.organizationId &&
        prev.tenantId === detail.tenantId
      ) {
        return
      }
      scheduleRouterRefresh(router)
    })
  }, [router])
  const queryClient = useQueryClient()
  const perspectiveConfig = perspective ?? null
  const perspectiveTableId = perspectiveConfig?.tableId ?? null
  const perspectiveEnabled = Boolean(perspectiveTableId)
  const initialSnapshotRef = React.useRef<PerspectiveSnapshot | null>(null)
  const snapshotTableIdRef = React.useRef<string | null>(null)
  if (typeof window !== 'undefined') {
    if (perspectiveTableId !== snapshotTableIdRef.current) {
      initialSnapshotRef.current = perspectiveTableId ? readPerspectiveSnapshot(perspectiveTableId) : null
      snapshotTableIdRef.current = perspectiveTableId ?? null
    }
  } else if (snapshotTableIdRef.current !== perspectiveTableId) {
    snapshotTableIdRef.current = perspectiveTableId ?? null
    initialSnapshotRef.current = null
  }
  const initialSnapshot = initialSnapshotRef.current
  const initialSettingsFromConfig = sanitizePerspectiveSettings(perspectiveConfig?.initialState?.initialSettings ?? null)
  const initialSettingsFromSnapshot = sanitizePerspectiveSettings(initialSnapshot?.settings ?? null)
  const mergedInitialSettings = initialSettingsFromConfig ?? initialSettingsFromSnapshot ?? null
  const initialActiveId = perspectiveConfig?.initialState?.activePerspectiveId ?? initialSnapshot?.perspectiveId ?? null
  const [isPerspectiveOpen, setPerspectiveOpen] = React.useState(false)
  const [activePerspectiveId, setActivePerspectiveId] = React.useState<string | null>(initialActiveId)
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => mergedInitialSettings?.columnVisibility ?? {})
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => mergedInitialSettings?.columnOrder ?? [])
  const [deletingIds, setDeletingIds] = React.useState<string[]>([])
  const [roleClearingIds, setRoleClearingIds] = React.useState<string[]>([])
  const [perspectiveApiMissing, setPerspectiveApiMissing] = React.useState(false)

  const perspectiveFeatureQuery = useQuery<{ use: boolean; roleDefaults: boolean }>({
    queryKey: ['feature-check', 'perspectives'],
    enabled: perspectiveEnabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      try {
        const call = await apiCall<{ granted?: unknown[] }>(
          '/api/auth/feature-check',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ features: ['perspectives.use', 'perspectives.role_defaults'] }),
          },
        )
        if (!call.ok) throw new Error(`feature-check failed (${call.status})`)
        const data = call.result ?? {}
        const granted = Array.isArray(data?.granted) ? data.granted.map((f: any) => String(f)) : []
        const has = (feature: string) => granted.some((grantedFeature: string) => {
          if (grantedFeature === '*') return true
          if (grantedFeature === feature) return true
          if (grantedFeature.endsWith('.*')) {
            const prefix = grantedFeature.slice(0, -2)
            return feature === prefix || feature.startsWith(`${prefix}.`)
          }
          return false
        })
        return {
          use: has('perspectives.use'),
          roleDefaults: has('perspectives.role_defaults'),
        }
      } catch {
        return {
          use: true,
          roleDefaults: true,
        }
      }
    },
  })
  const perspectivePermissions = perspectiveFeatureQuery.data
  const canUsePerspectives = perspectiveEnabled && Boolean(perspectivePermissions?.use)
  const canUseRoleDefaultsFeature = Boolean(perspectivePermissions?.roleDefaults)

  React.useEffect(() => {
    if (!canUsePerspectives && isPerspectiveOpen) {
      setPerspectiveOpen(false)
    }
  }, [canUsePerspectives, isPerspectiveOpen])

  React.useEffect(() => {
    if (!perspectiveTableId) return
    if (!mergedInitialSettings) return
    const snapshot: PerspectiveSnapshot = {
      perspectiveId: initialActiveId,
      settings: mergedInitialSettings,
      updatedAt: Date.now(),
    }
    writePerspectiveSnapshot(perspectiveTableId, snapshot)
    initialSnapshotRef.current = snapshot
  }, [perspectiveTableId, mergedInitialSettings, initialActiveId])

  const perspectiveQuery = useQuery<PerspectivesIndexResponse>({
    queryKey: ['table-perspectives', perspectiveTableId],
    queryFn: async () => {
      if (!perspectiveTableId) throw new Error('Missing table id')
      const call = await apiCall<PerspectivesIndexResponse>(`/api/perspectives/${encodeURIComponent(perspectiveTableId)}`)
      if (call.status === 404) {
        setPerspectiveApiMissing(true)
        return {
          tableId: perspectiveTableId,
          perspectives: [],
          defaultPerspectiveId: null,
          rolePerspectives: [],
          roles: [],
          canApplyToRoles: false,
        }
      }
      if (!call.ok) {
        await raiseCrudError(call.response, t('ui.dataTable.perspectives.error.load', 'Failed to load perspectives'))
      }
      setPerspectiveApiMissing(false)
      const payload = call.result
      if (!payload) throw new Error(t('ui.dataTable.perspectives.error.load', 'Failed to load perspectives'))
      return payload
    },
    enabled: canUsePerspectives,
    initialData: perspectiveConfig?.initialState?.response,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
  const perspectiveData = perspectiveQuery.data
  const initialPerspectiveAppliedRef = React.useRef(Boolean(mergedInitialSettings))

  const extensionTableId = React.useMemo(() => {
    if (perspective?.tableId) return perspective.tableId
    if (injectionSpotId?.startsWith('data-table:')) return injectionSpotId.slice('data-table:'.length)
    return null
  }, [injectionSpotId, perspective?.tableId])
  const resolvedInjectionSpotId = injectionSpotId ?? (perspective?.tableId ? `data-table:${perspective.tableId}` : null)
  const resolvedReplacementHandle = replacementHandle ?? ComponentReplacementHandles.dataTable(extensionTableId ?? 'unknown')
  const resolvedInjectionContext = React.useMemo(
    () => injectionContext ?? { tableId: perspective?.tableId ?? null, title: typeof title === 'string' ? title : undefined },
    [injectionContext, perspective?.tableId, title]
  )
  const headerInjectionSpotId = React.useMemo(
    () => (resolvedInjectionSpotId ? `${resolvedInjectionSpotId}:header` : null),
    [resolvedInjectionSpotId]
  )
  const footerInjectionSpotId = React.useMemo(
    () => (resolvedInjectionSpotId ? `${resolvedInjectionSpotId}:footer` : null),
    [resolvedInjectionSpotId]
  )
  const { widgets: columnWidgets } = useInjectionDataWidgets(
    extensionTableId ? `data-table:${extensionTableId}:columns` : '__disabled__:columns',
  )
  const { widgets: rowActionWidgets } = useInjectionDataWidgets(
    extensionTableId ? `data-table:${extensionTableId}:row-actions` : '__disabled__:row-actions',
  )
  const { widgets: bulkActionWidgets } = useInjectionDataWidgets(
    extensionTableId ? `data-table:${extensionTableId}:bulk-actions` : '__disabled__:bulk-actions',
  )
  const { widgets: filterWidgets } = useInjectionDataWidgets(
    extensionTableId ? `data-table:${extensionTableId}:filters` : '__disabled__:filters',
  )
  const injectedColumnDefs = React.useMemo<{ def: ColumnDef<T, unknown>; placement: InjectionColumnDefinition['placement'] }[]>(() => {
    const entries: InjectionColumnDefinition[] = []
    for (const widget of columnWidgets) {
      if (!('columns' in widget)) continue
      for (const definition of widget.columns ?? []) {
        entries.push(definition)
      }
    }
    return collectUniqueById(entries, 'column').map((definition) => ({
      def: {
        id: definition.id,
        accessorFn: (row: T) => readInjectedColumnValue(row, definition.accessorKey),
        header: t(definition.header, definition.header),
        cell: definition.cell as ColumnDef<T, unknown>['cell'],
        size: definition.size,
        enableSorting: definition.sortable === true,
      },
      placement: definition.placement,
    }))
  }, [columnWidgets, t])
  const injectedRowActions = React.useMemo<InjectionRowActionDefinition[]>(() => {
    const entries: InjectionRowActionDefinition[] = []
    for (const widget of rowActionWidgets) {
      if (!('rowActions' in widget)) continue
      for (const definition of widget.rowActions ?? []) {
        entries.push(definition)
      }
    }
    return collectUniqueById(entries, 'row action')
  }, [rowActionWidgets])
  const injectedBulkActions = React.useMemo<InjectionBulkActionDefinition[]>(() => {
    const entries: InjectionBulkActionDefinition[] = []
    for (const widget of bulkActionWidgets) {
      if (!('bulkActions' in widget)) continue
      for (const definition of widget.bulkActions ?? []) {
        entries.push(definition)
      }
    }
    return collectUniqueById(entries, 'bulk action')
  }, [bulkActionWidgets])
  const { serverFilters: injectedFilters, clientFilters: injectedClientFilters } = React.useMemo<{
    serverFilters: FilterDef[]
    clientFilters: { id: string; filterFn: (row: unknown, value: unknown) => boolean }[]
  }>(() => {
    const byId = new Map<string, FilterDef>()
    const clientEntries: { id: string; filterFn: (row: unknown, value: unknown) => boolean }[] = []
    for (const widget of filterWidgets) {
      if (!('filters' in widget)) continue
      for (const definition of widget.filters ?? []) {
        const filter = definition as InjectionFilterDefinition
        const mappedType: FilterDef['type'] =
          filter.type === 'date-range'
            ? 'dateRange'
            : filter.type === 'boolean'
              ? 'checkbox'
              : filter.type === 'select'
                ? 'select'
                : 'text'
        const id = filter.queryParam ?? filter.id
        if (filter.strategy === 'client' && filter.filterFn) {
          clientEntries.push({ id, filterFn: filter.filterFn })
        }
        if (!byId.has(id)) {
          const translatedOptions = Array.isArray(filter.options)
            ? filter.options.map((option) => ({
                ...option,
                label: t(option.label, option.label),
              }))
            : filter.options
          byId.set(id, {
            id,
            label: t(filter.label, filter.label),
            type: mappedType,
            options: translatedOptions,
          })
        }
      }
    }
    return { serverFilters: Array.from(byId.values()), clientFilters: clientEntries }
  }, [filterWidgets, t])
  const mergedColumns = React.useMemo<ColumnDef<T, unknown>[]>(() => {
    if (!injectedColumnDefs.length) return columns
    let result = [...columns]
    for (const { def, placement } of injectedColumnDefs) {
      result = insertByInjectionPlacement(
        result,
        def,
        placement,
        (col) => (col as { id?: string }).id ?? '',
      )
    }
    return result
  }, [columns, injectedColumnDefs])
  const resolvedRowActions = React.useCallback((row: T) => {
    const injectedItems: (RowActionItem & { placement?: InjectionRowActionDefinition['placement'] })[] = injectedRowActions.map((action) => ({
      id: action.id,
      label: t(action.label, action.label),
      onSelect: () => action.onSelect(row, { navigate: (href: string) => router.push(href) }),
      placement: action.placement,
    }))
    const baseNode = rowActions ? rowActions(row) : null
    if (!injectedItems.length) return baseNode
    if (React.isValidElement(baseNode)) {
      const baseItems = (baseNode.props as { items?: RowActionItem[] }).items
      if (Array.isArray(baseItems)) {
        let merged = [...baseItems]
        const existingIds = new Set(
          baseItems.map((item) => item.id).filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
        for (const item of injectedItems) {
          if (item.id && existingIds.has(item.id)) continue
          merged = insertByInjectionPlacement(
            merged,
            item,
            item.placement,
            (entry) => entry.id ?? '',
          )
        }
        return <RowActions items={merged} />
      }
    }
    return <RowActions items={injectedItems} />
  }, [injectedRowActions, rowActions, router, t])

  // Date formatting setup
  const DATE_FORMAT = (process.env.NEXT_PUBLIC_DATE_FORMAT || 'YYYY-MM-DD HH:mm') as string

  const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n))
  const simpleFormat = (d: Date, fmt: string) => {
    // Supports tokens: YYYY, MM, DD, HH, mm, ss
    const YYYY = String(d.getFullYear())
    const MM = pad2(d.getMonth() + 1)
    const DD = pad2(d.getDate())
    const HH = pad2(d.getHours())
    const mm = pad2(d.getMinutes())
    const ss = pad2(d.getSeconds())
    return fmt
      .replace(/YYYY/g, YYYY)
      .replace(/MM/g, MM)
      .replace(/DD/g, DD)
      .replace(/HH/g, HH)
      .replace(/mm/g, mm)
      .replace(/ss/g, ss)
  }

  const tryParseDate = (v: unknown): Date | null => {
    if (v == null) return null
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v
    if (typeof v === 'number') {
      const d = new Date(v)
      return isNaN(d.getTime()) ? null : d
    }
    if (typeof v === 'string') {
      const s = v.trim()
      if (!s) return null
      // ISO-like detection (YYYY-MM-DD ...)
      if (/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(s)) {
        const d = new Date(s)
        return isNaN(d.getTime()) ? null : d
      }
      // Fallback: Date.parse
      const d = new Date(s)
      return isNaN(d.getTime()) ? null : d
    }
    return null
  }

  // Guess date columns once using first non-empty row
  const [dateColumnIds, setDateColumnIds] = React.useState<Set<string> | null>(null)
  React.useEffect(() => {
    if (dateColumnIds) return
    if (!data || data.length === 0) return
    // Build a cheap row accessor using column defs
    const accessors = mergedColumns.map((c) => {
      const key = (c as any).accessorKey as string | undefined
      const id = (c as any).id as string | undefined
      return { id: id || key || '', key }
    })
    const guessed = new Set<string>()
    accessors.forEach((a) => {
      if (!a.id) return
      const name = a.id
      // Name-based guess: snake_case '_at' suffix
      if (name.endsWith('_at')) {
        guessed.add(name)
        return
      }
    })
    setDateColumnIds(guessed)
  }, [dateColumnIds, data, mergedColumns])
  // Column visibility: only hide columns explicitly marked as hidden.
  // All other columns are always rendered; horizontal scroll (min-w + overflow-auto)
  // handles narrow viewports so users can swipe to reach every column.
  const responsiveClass = (_priority?: number, hidden?: boolean) => {
    if (hidden) return 'hidden'
    return ''
  }

  const resolvePriority = React.useCallback((column: TableColumn<T, unknown>) => {
    const meta = (column.columnDef as any)?.meta
    const rawPriority = typeof meta?.priority === 'number' ? meta.priority : undefined
    if (rawPriority && rawPriority > 0) return rawPriority
    const index = column.getIndex()
    return index <= 1 ? 1 : 2
  }, [])

  const initialSorting = React.useMemo<SortingState>(() => {
    if (mergedInitialSettings?.sorting) {
      return mergedInitialSettings.sorting.map((item) => ({ id: item.id, desc: Boolean(item.desc) }))
    }
    return []
  }, [mergedInitialSettings])
  const [sorting, setSorting] = React.useState<SortingState>(() => {
    if (sortingProp && sortingProp.length) return sortingProp
    if (initialSorting.length) return initialSorting
    return []
  })
  const clientFilteredData = React.useMemo(() => {
    if (!injectedClientFilters.length || !filterValues) return data
    const activeClientFilters = injectedClientFilters.filter(
      (cf) => filterValues[cf.id] !== undefined && filterValues[cf.id] !== '' && filterValues[cf.id] !== null,
    )
    if (!activeClientFilters.length) return data
    return data.filter((row) =>
      activeClientFilters.every((cf) => cf.filterFn(row, filterValues[cf.id])),
    )
  }, [data, injectedClientFilters, filterValues])
  const hasInjectedBulkActions = injectedBulkActions.length > 0
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const table = useReactTable<T>({
    data: clientFilteredData,
    columns: mergedColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(sortable ? { getSortedRowModel: getSortedRowModel() } : {}),
    state: { sorting, columnVisibility, columnOrder, rowSelection },
    enableRowSelection: hasInjectedBulkActions,
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      setSorting(next)
      onSortingChange?.(next)
    },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater
      setColumnVisibility(next)
    },
    onColumnOrderChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnOrder) : updater
      setColumnOrder(next)
    },
    onRowSelectionChange: setRowSelection,
  })
  React.useEffect(() => { if (sortingProp) setSorting(sortingProp) }, [sortingProp])
  React.useEffect(() => {
    if (hasInjectedBulkActions) return
    if (Object.keys(rowSelection).length === 0) return
    setRowSelection({})
  }, [hasInjectedBulkActions, rowSelection])
  React.useEffect(() => {
    const ids = table.getAllLeafColumns().map((column) => column.id)
    if (!ids.length) return
    setColumnOrder((prev) => {
      if (!prev.length) return ids
      const allowed = ids
      const filtered = prev.filter((id) => allowed.includes(id))
      const seen = new Set(filtered)
      for (const id of allowed) {
        if (!seen.has(id)) {
          filtered.push(id)
          seen.add(id)
        }
      }
      const changed = filtered.length !== prev.length || filtered.some((id, index) => id !== prev[index])
      return changed ? filtered : prev
    })
  }, [table, mergedColumns])

  const initialVisibilityApplied = React.useRef(Boolean(mergedInitialSettings?.columnVisibility))
  React.useEffect(() => {
    if (initialVisibilityApplied.current) return
    const hidden: VisibilityState = {}
    table.getAllLeafColumns().forEach((column) => {
      const hiddenMeta = (column.columnDef as any)?.meta?.hidden
      if (hiddenMeta) hidden[column.id] = false
    })
    if (Object.keys(hidden).length) {
      setColumnVisibility((prev) => ({ ...hidden, ...prev }))
    }
    initialVisibilityApplied.current = true
  }, [table, mergedColumns])

  const getCurrentSettings = React.useCallback((): PerspectiveSettings => {
    const visibility: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(columnVisibility)) {
      if (typeof key === 'string' && typeof value === 'boolean') {
        visibility[key] = value
      }
    }
    const filtersRecord: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(filterValues ?? {})) {
      if (typeof key === 'string') filtersRecord[key] = value
    }
    const candidate: PerspectiveSettings = {
      columnOrder,
      columnVisibility: visibility,
      sorting,
      filters: filtersRecord,
      searchValue,
    }
    return sanitizePerspectiveSettings(candidate) ?? {}
  }, [columnOrder, columnVisibility, sorting, filterValues, searchValue])

  const applyPerspectiveSettings = React.useCallback((settings: PerspectiveSettings, nextId: string | null) => {
    const normalized = sanitizePerspectiveSettings(settings) ?? {}
    if (normalized.columnOrder && normalized.columnOrder.length) {
      setColumnOrder(normalized.columnOrder)
    } else {
      const ids = table.getAllLeafColumns().map((column) => column.id)
      if (ids.length) setColumnOrder(ids)
    }
    if (normalized.columnVisibility) setColumnVisibility(normalized.columnVisibility)
    else setColumnVisibility({})
    if (normalized.sorting) {
      const sortingState: SortingState = normalized.sorting.map((item) => ({
        id: item.id,
        desc: item.desc === true,
      }))
      setSorting(sortingState)
      onSortingChange?.(sortingState)
    } else {
      setSorting([])
      onSortingChange?.([])
    }
    if (onFiltersApply) {
      onFiltersApply((normalized.filters ?? {}) as FilterValues)
    }
    if (onSearchChange) {
      onSearchChange(normalized.searchValue ?? '')
    }
    setActivePerspectiveId(nextId)
    if (perspectiveTableId) {
      writePerspectiveCookie(perspectiveTableId, nextId)
      if (nextId) {
        const snapshot: PerspectiveSnapshot = { perspectiveId: nextId, settings: normalized, updatedAt: Date.now() }
        writePerspectiveSnapshot(perspectiveTableId, snapshot)
        initialSnapshotRef.current = snapshot
      } else {
        writePerspectiveSnapshot(perspectiveTableId, null)
        initialSnapshotRef.current = null
      }
    }
  }, [onFiltersApply, onSearchChange, onSortingChange, perspectiveTableId, table])

  type SavePerspectivePayload = {
    name: string
    isDefault: boolean
    applyToRoles: string[]
    setRoleDefault: boolean
    perspectiveId?: string | null
  }

  const perspectiveQueryKey: [string, string | null] = ['table-perspectives', perspectiveTableId]
  const savePerspectiveMutation = useMutation<PerspectiveSaveResponse, Error, SavePerspectivePayload>({
    mutationFn: async (input) => {
      if (!perspectiveTableId) throw new Error('Missing table id')
      const payload = {
        perspectiveId: input.perspectiveId ?? undefined,
        name: input.name,
        settings: getCurrentSettings(),
        isDefault: input.isDefault,
        applyToRoles: input.applyToRoles,
        setRoleDefault: input.setRoleDefault,
      }
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[DataTable] perspective payload', payload)
      }
      const call = await apiCall<PerspectiveSaveResponse>(
        `/api/perspectives/${encodeURIComponent(perspectiveTableId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      )
      if (call.status === 404) {
        throw new Error(t('ui.dataTable.perspectives.error.apiUnavailable', 'Perspectives API is not available. Run `npm run modules:prepare` to regenerate module routes and restart the dev server.'))
      }
      if (!call.ok) {
        await raiseCrudError(call.response, t('ui.dataTable.perspectives.error.save', 'Failed to save perspective'))
      }
      const result = call.result
      if (!result) throw new Error(t('ui.dataTable.perspectives.error.save', 'Failed to save perspective'))
      return result
    },
    onSuccess: (data) => {
      if (perspectiveTableId) {
        void queryClient.invalidateQueries({ queryKey: perspectiveQueryKey })
      }
      if (data.perspective) {
        applyPerspectiveSettings(data.perspective.settings, data.perspective.id)
      }
    },
  })

  const resolveColumnLabel = React.useCallback((column: TableColumn<T, unknown>): string => {
    const meta = (column.columnDef as any)?.meta
    if (typeof meta?.label === 'string' && meta.label.trim().length > 0) return meta.label.trim()
    if (typeof meta?.title === 'string' && meta.title.trim().length > 0) return meta.title.trim()
    const header = column.columnDef.header
    if (typeof header === 'string') return header
    if (typeof header === 'function') return normalizeLabel(column.id)
    return normalizeLabel(column.id)
  }, [])

  const columnOptions = React.useMemo(() => {
    const leaves = table.getAllLeafColumns()
    const baseOrder = columnOrder.length ? columnOrder : leaves.map((column) => column.id)
    const seen = new Set<string>()
    const ordered = baseOrder
      .map((id) => {
        const col = leaves.find((column) => column.id === id)
        if (!col) return null
        seen.add(id)
        return col
      })
      .filter(Boolean) as Array<TableColumn<T, unknown>>
    leaves.forEach((column) => { if (!seen.has(column.id)) ordered.push(column) })
    return ordered.map((column) => ({
      id: column.id,
      label: resolveColumnLabel(column),
      visible: columnVisibility[column.id] ?? column.getIsVisible(),
      canHide: column.getCanHide(),
    }))
  }, [table, columnOrder, resolveColumnLabel, columnVisibility, columns])

  const activePersonalPerspectiveId = React.useMemo(() => {
    if (!perspectiveData || !activePerspectiveId) return null
    const found = perspectiveData.perspectives.find((p) => p.id === activePerspectiveId)
    return found ? found.id : null
  }, [perspectiveData, activePerspectiveId])


  const deletePerspectiveMutation = useMutation<void, Error, { perspectiveId: string }>({
    mutationFn: async ({ perspectiveId }) => {
      if (!perspectiveTableId) throw new Error('Missing table id')
      const call = await apiCall(
        `/api/perspectives/${encodeURIComponent(perspectiveTableId)}/${encodeURIComponent(perspectiveId)}`,
        { method: 'DELETE' },
      )
      if (call.status === 404) throw new Error(t('ui.dataTable.perspectives.error.apiUnavailable', 'Perspectives API is not available. Run `npm run modules:prepare` and restart the dev server.'))
      if (!call.ok) {
        await raiseCrudError(call.response, t('ui.dataTable.perspectives.error.delete', 'Failed to delete perspective'))
      }
    },
    onMutate: ({ perspectiveId }) => {
      setDeletingIds((prev) => prev.includes(perspectiveId) ? prev : [...prev, perspectiveId])
    },
    onSettled: (_data, _error, variables) => {
      setDeletingIds((prev) => prev.filter((id) => id !== variables.perspectiveId))
    },
    onSuccess: (_data, variables) => {
      const removedActive = activePerspectiveId === variables.perspectiveId
      if (perspectiveTableId) {
        void queryClient.invalidateQueries({ queryKey: perspectiveQueryKey })
        if (removedActive) {
          setActivePerspectiveId(null)
          writePerspectiveCookie(perspectiveTableId, null)
          writePerspectiveSnapshot(perspectiveTableId, null)
          initialSnapshotRef.current = null
          initialPerspectiveAppliedRef.current = false
        }
      } else if (removedActive) {
        setActivePerspectiveId(null)
        initialPerspectiveAppliedRef.current = false
      }
    },
  })

  const clearRoleMutation = useMutation<void, Error, { roleId: string }>({
    mutationFn: async ({ roleId }) => {
      if (!perspectiveTableId) throw new Error('Missing table id')
      const call = await apiCall(
        `/api/perspectives/${encodeURIComponent(perspectiveTableId)}/roles/${encodeURIComponent(roleId)}`,
        { method: 'DELETE' },
      )
      if (call.status === 404) throw new Error(t('ui.dataTable.perspectives.error.apiUnavailable', 'Perspectives API is not available. Run `npm run modules:prepare` and restart the dev server.'))
      if (!call.ok) {
        await raiseCrudError(call.response, t('ui.dataTable.perspectives.error.clearRoles', 'Failed to clear role perspectives'))
      }
    },
    onMutate: ({ roleId }) => {
      setRoleClearingIds((prev) => prev.includes(roleId) ? prev : [...prev, roleId])
    },
    onSettled: (_data, _error, variables) => {
      setRoleClearingIds((prev) => prev.filter((id) => id !== variables.roleId))
    },
    onSuccess: (_data, variables) => {
      if (perspectiveTableId) {
        void queryClient.invalidateQueries({ queryKey: perspectiveQueryKey })
      }
      if (activePerspectiveId) {
        const current = queryClient.getQueryData<PerspectivesIndexResponse>(perspectiveQueryKey)
        const match = current?.rolePerspectives.find((rp) => rp.id === activePerspectiveId)
        if (match && match.roleId === variables.roleId) {
          setActivePerspectiveId(null)
          if (perspectiveTableId) writePerspectiveCookie(perspectiveTableId, null)
          if (perspectiveTableId) writePerspectiveSnapshot(perspectiveTableId, null)
          initialSnapshotRef.current = null
          initialPerspectiveAppliedRef.current = false
        }
      }
    },
  })

  const handlePerspectiveActivate = React.useCallback((item: PerspectiveDto | RolePerspectiveDto, _source?: 'personal' | 'role') => {
    applyPerspectiveSettings(item.settings, item.id)
    setPerspectiveOpen(false)
  }, [applyPerspectiveSettings])

  const handlePerspectiveSave = React.useCallback(async (input: { name: string; isDefault: boolean; applyToRoles: string[]; setRoleDefault: boolean }) => {
    const normalizedRoles = Array.from(new Set(input.applyToRoles))
    await savePerspectiveMutation.mutateAsync({
      name: input.name.trim(),
      isDefault: input.isDefault,
      applyToRoles: normalizedRoles,
      setRoleDefault: normalizedRoles.length > 0 ? input.setRoleDefault : false,
      perspectiveId: activePersonalPerspectiveId,
    })
  }, [savePerspectiveMutation, activePersonalPerspectiveId])

  const handlePerspectiveDelete = React.useCallback(async (perspectiveId: string) => {
    await deletePerspectiveMutation.mutateAsync({ perspectiveId })
  }, [deletePerspectiveMutation])

  const handleClearRole = React.useCallback(async (roleId: string) => {
    await clearRoleMutation.mutateAsync({ roleId })
  }, [clearRoleMutation])

  const handleToggleColumn = React.useCallback((columnId: string, visible: boolean) => {
    const column = table.getColumn(columnId)
    if (!column) return
    setColumnVisibility((prev) => {
      const next = { ...prev }
      if (visible) delete next[columnId]
      else next[columnId] = false
      return next
    })
    column.toggleVisibility(visible)
  }, [table])

  const handleMoveColumn = React.useCallback((columnId: string, direction: 'up' | 'down') => {
    setColumnOrder((prev) => {
      const idx = prev.indexOf(columnId)
      if (idx === -1) return prev
      const swap = direction === 'up' ? idx - 1 : idx + 1
      if (swap < 0 || swap >= prev.length) return prev
      const next = [...prev]
      const tmp = next[swap]
      next[swap] = next[idx]
      next[idx] = tmp
      table.setColumnOrder(next)
      return next
    })
  }, [table])

  const perspectiveApiWarning = perspectiveApiMissing && canUsePerspectives
    ? t('ui.dataTable.perspectives.warning.apiUnavailable', 'Perspectives API is not available yet. Run `npm run modules:prepare` to regenerate module routes, then restart the server.')
    : null

  const loadStartRef = React.useRef<number | null>(null)
  const [measuredDurationMs, setMeasuredDurationMs] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (typeof isLoading !== 'boolean') return
    if (isLoading) {
      if (loadStartRef.current === null) {
        const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now()
        loadStartRef.current = now
      }
      return
    }
    if (loadStartRef.current !== null) {
      const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
      setMeasuredDurationMs(now - loadStartRef.current)
      loadStartRef.current = null
    }
  }, [isLoading])

  React.useLayoutEffect(() => {
    if (!canUsePerspectives) return
    if (!perspectiveTableId) return
    if (initialPerspectiveAppliedRef.current && activePerspectiveId != null) return

    const source = perspectiveData ?? perspectiveConfig?.initialState?.response
    if (!source) return

    const tryResolve = (id: string | null | undefined): PerspectiveDto | RolePerspectiveDto | undefined => {
      if (!id) return undefined
      return source.perspectives.find((p) => p.id === id)
        ?? source.rolePerspectives.find((p) => p.id === id)
    }

    let target: PerspectiveDto | RolePerspectiveDto | undefined
    if (activePerspectiveId) {
      target = tryResolve(activePerspectiveId)
    }
    const cookieId = readPerspectiveCookie(perspectiveTableId)
    if (!target && cookieId) target = tryResolve(cookieId)
    if (!target && source.defaultPerspectiveId) {
      target = tryResolve(source.defaultPerspectiveId)
    }
    if (!target) {
      target = source.rolePerspectives.find((p) => p.isDefault)
    }
    if (!target) {
      target = source.perspectives[0]
    }
    if (target) {
      applyPerspectiveSettings(target.settings, target.id)
    }
    initialPerspectiveAppliedRef.current = true
  }, [canUsePerspectives, perspectiveData, perspectiveTableId, perspectiveConfig, applyPerspectiveSettings, activePerspectiveId])

  const scrollTableIntoView = React.useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect || rect.top >= 0) return
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
    containerRef.current?.scrollIntoView({ behavior, block: 'start' })
  }, [])

  const paginationNode = React.useMemo(() => {
    if (!pagination || pagination.total === 0) return null

    const { page, totalPages, onPageChange, durationMs, cacheStatus } = pagination
    const startItem = (page - 1) * pagination.pageSize + 1
    const endItem = Math.min(page * pagination.pageSize, pagination.total)
    const effectiveDuration = (typeof durationMs === 'number' && Number.isFinite(durationMs) && durationMs >= 0)
      ? durationMs
      : measuredDurationMs ?? undefined
    const durationLabel = formatDurationLabel(effectiveDuration)
    const normalizedCacheStatus = cacheStatus === 'hit' || cacheStatus === 'miss' ? cacheStatus : null
    const cacheBadge = normalizedCacheStatus ? (
      <span
        className="inline-flex items-center justify-center"
        aria-label={t('ui.dataTable.pagination.cache.ariaLabel', 'Cache {status}', { status: normalizedCacheStatus.toUpperCase() })}
        title={t('ui.dataTable.pagination.cache.title', 'Cache {status}', { status: normalizedCacheStatus.toUpperCase() })}
      >
        <Circle
          className={`h-3.5 w-3.5 ${normalizedCacheStatus === 'hit' ? 'text-emerald-500' : 'text-amber-500'}`}
          strokeWidth={3}
        />
        <span className="sr-only">{t('ui.dataTable.pagination.cache.srOnly', 'Cache {status}', { status: normalizedCacheStatus.toUpperCase() })}</span>
      </span>
    ) : null

    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t">
        <div className="text-sm text-muted-foreground flex items-center justify-center sm:justify-start gap-2">
          <span>
            {durationLabel
              ? t('ui.dataTable.pagination.resultsWithDuration', 'Showing {start} to {end} of {total} results in {duration}', { start: startItem, end: endItem, total: pagination.total, duration: durationLabel })
              : t('ui.dataTable.pagination.results', 'Showing {start} to {end} of {total} results', { start: startItem, end: endItem, total: pagination.total })
            }
          </span>
          {cacheBadge}
        </div>
        <div className="flex items-center justify-center sm:justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { onPageChange(page - 1); scrollTableIntoView() }}
            disabled={page <= 1}
          >
            {t('ui.dataTable.pagination.previous', 'Previous')}
          </Button>
          <span className="text-sm whitespace-nowrap">
            {t('ui.dataTable.pagination.pageInfo', 'Page {page} of {totalPages}', { page, totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { onPageChange(page + 1); scrollTableIntoView() }}
            disabled={page >= totalPages}
          >
            {t('ui.dataTable.pagination.next', 'Next')}
          </Button>
        </div>
      </div>
    )
  }, [pagination, measuredDurationMs, scrollTableIntoView, t])

  // Auto filters: fetch custom field defs when requested
  const resolvedEntityIds = React.useMemo(() => {
    if (Array.isArray(entityIds) && entityIds.length) {
      const dedup = new Set<string>()
      const list: string[] = []
      entityIds.forEach((id) => {
        const trimmed = typeof id === 'string' ? id.trim() : ''
        if (!trimmed || dedup.has(trimmed)) return
        dedup.add(trimmed)
        list.push(trimmed)
      })
      return list
    }
    if (typeof entityId === 'string' && entityId.trim().length > 0) {
      return [entityId.trim()]
    }
    return []
  }, [entityId, entityIds])
  const entityKey = React.useMemo(() => (resolvedEntityIds.length ? resolvedEntityIds.join('|') : null), [resolvedEntityIds])
  const customFieldFilterExtrasSignature = React.useMemo(
    () => JSON.stringify(customFieldFilterKeyExtras ?? []),
    [customFieldFilterKeyExtras]
  )

  const [cfFilterFieldsetsByEntity, setCfFilterFieldsetsByEntity] = React.useState<Record<string, CustomFieldsetDto[]>>({})
  const [cfFilterFieldsetSelection, setCfFilterFieldsetSelection] = React.useState<Record<string, string | null>>({})

  React.useEffect(() => {
    if (!entityKey) {
      setCfFilterFieldsetsByEntity({})
      setCfFilterFieldsetSelection({})
      return
    }
    let cancelled = false
    const loadFieldsets = async () => {
      try {
        const payload = await fetchCustomFieldDefinitionsPayload(resolvedEntityIds)
        if (cancelled) return
        const fieldsets = payload.fieldsetsByEntity ?? {}
        setCfFilterFieldsetsByEntity(fieldsets)
        const selectionChanges: Array<[string, string | null]> = []
        let shouldNotify = false
        setCfFilterFieldsetSelection((prev) => {
          const next: Record<string, string | null> = {}
          let changed = false
          resolvedEntityIds.forEach((entityId) => {
            const list = fieldsets[entityId] ?? []
            if (!list.length) {
              if (prev[entityId] !== undefined) changed = true
              return
            }
            const existing = prev[entityId]
            const fallback = list[0]?.code ?? null
            const isValidExisting = existing ? list.some((entry) => entry.code === existing) : false
            const value = isValidExisting ? existing : fallback ?? null
            next[entityId] = value
            if (value !== existing) {
              changed = true
              selectionChanges.push([entityId, value])
            }
          })
          if (Object.keys(prev).length !== Object.keys(next).length) changed = true
          if (changed) {
            shouldNotify = true
            return next
          }
          return prev
        })
        if (shouldNotify && selectionChanges.length && onCustomFieldFilterFieldsetChange) {
          selectionChanges.forEach(([entityId, value]) => onCustomFieldFilterFieldsetChange(value, entityId))
        }
      } catch {
        if (!cancelled) {
          setCfFilterFieldsetsByEntity({})
          setCfFilterFieldsetSelection({})
        }
      }
    }
    loadFieldsets()
    return () => {
      cancelled = true
    }
  }, [customFieldFilterExtrasSignature, entityKey, onCustomFieldFilterFieldsetChange, resolvedEntityIds])

  const supportsCustomFieldFilterFieldsets =
    resolvedEntityIds.length === 1 &&
    (cfFilterFieldsetsByEntity[resolvedEntityIds[0]]?.length ?? 0) > 0
  const activeCustomFieldFilterFieldset = supportsCustomFieldFilterFieldsets
    ? cfFilterFieldsetSelection[resolvedEntityIds[0]] ?? cfFilterFieldsetsByEntity[resolvedEntityIds[0]]?.[0]?.code ?? null
    : null

  const handleCustomFieldFilterFieldsetChange = React.useCallback(
    (value: string) => {
      if (!supportsCustomFieldFilterFieldsets) return
      const entityId = resolvedEntityIds[0]
      const nextValue = value || null
      setCfFilterFieldsetSelection((prev) => {
        if (prev[entityId] === nextValue) return prev
        return { ...prev, [entityId]: nextValue }
      })
      if (onCustomFieldFilterFieldsetChange) {
        onCustomFieldFilterFieldsetChange(nextValue, entityId)
      }
    },
    [onCustomFieldFilterFieldsetChange, resolvedEntityIds, supportsCustomFieldFilterFieldsets],
  )

  const { data: cfFilters = [] } = useCustomFieldFilterDefs(entityKey ? resolvedEntityIds : [], {
    enabled: !!entityKey,
    fieldset: supportsCustomFieldFilterFieldsets ? activeCustomFieldFilterFieldset ?? undefined : undefined,
    keyExtras: customFieldFilterKeyExtras,
  })

  const selectedRows = React.useMemo<T[]>(() => {
    if (!hasInjectedBulkActions) return []
    return table.getSelectedRowModel().rows.map((row) => row.original as T)
  }, [hasInjectedBulkActions, table, rowSelection])

  const runBulkAction = React.useCallback(
    async (action: InjectionBulkActionDefinition) => {
      if (action.requiresSelection !== false && !selectedRows.length) return
      try {
        const result = await action.onExecute(selectedRows, {
          tableId: extensionTableId,
          navigate: (href: string) => router.push(href),
          confirm,
          refresh: refreshButton?.onRefresh,
          injectionContext: resolvedInjectionContext,
          translate: t,
        })
        const normalized = result as BulkActionExecuteResult | void
        if (normalized && normalized.ok === false) {
          if (normalized.message === undefined) return
          flash(
            normalized.message
              ?? t('ui.dataTable.bulkAction.error', 'Bulk action failed.'),
            'error',
          )
          return
        }
        if (normalized?.progressJobId) {
          setRowSelection({})
          flash(
            normalized.message
              ?? t('ui.dataTable.bulkAction.started', 'Bulk action started. Track progress in the top bar.'),
            'success',
          )
          return
        }
        flash(
          normalized?.message
            ?? t('ui.dataTable.bulkAction.success', 'Bulk action completed.'),
          'success',
        )
        setRowSelection({})
        if (refreshButton?.onRefresh) {
          refreshButton.onRefresh()
        } else {
          scheduleRouterRefresh(router)
        }
      } catch (error) {
        flash(
          error instanceof Error
            ? error.message
            : t('ui.dataTable.bulkAction.error', 'Bulk action failed.'),
          'error',
        )
      }
    },
    [confirm, extensionTableId, refreshButton, resolvedInjectionContext, router, selectedRows, t],
  )

  const builtToolbar = React.useMemo(() => {
    if (toolbar) return toolbar
    const anySearch = onSearchChange != null
    const anyFilters = (baseFilters && baseFilters.length > 0) || (cfFilters && cfFilters.length > 0) || injectedFilters.length > 0
    const hasBulkButtons = hasInjectedBulkActions
    if (!anySearch && !anyFilters && !hasBulkButtons) return null
    // Merge base filters with CF filters, preferring base definitions when ids collide
    const baseList = baseFilters || []
    const existing = new Set(baseList.map((f) => f.id))
    const cfOnly = (cfFilters || []).filter((f) => !existing.has(f.id))
    const injectedOnly = injectedFilters.filter((f) => !existing.has(f.id) && !cfOnly.some((cf) => cf.id === f.id))
    const combined: FilterDef[] = [...baseList, ...cfOnly, ...injectedOnly]
    const perspectiveButton = canUsePerspectives ? (
      <Button variant="outline" className="h-9" onClick={() => setPerspectiveOpen(true)}>
        <SlidersHorizontal className="mr-2 h-4 w-4" />
        {t('ui.dataTable.perspectives.button', 'Perspectives')}
      </Button>
    ) : null
    const fieldsetSelector =
      supportsCustomFieldFilterFieldsets && resolvedEntityIds.length === 1
        ? (
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('ui.dataTable.fieldset.label', 'Fieldset')}
            </div>
            <select
              className="w-full rounded border bg-background px-2 py-2 text-sm"
              value={activeCustomFieldFilterFieldset ?? ''}
              onChange={(event) => handleCustomFieldFilterFieldsetChange(event.target.value)}
            >
              {(cfFilterFieldsetsByEntity[resolvedEntityIds[0]] ?? []).map((fieldset) => (
                <option key={fieldset.code} value={fieldset.code}>
                  {fieldset.label}
                </option>
              ))}
            </select>
          </div>
        )
        : null
    const leadingItems = perspectiveButton ? <div className="flex items-center gap-2">{perspectiveButton}</div> : null
    const trailingItems = hasBulkButtons ? (
      <div className="flex flex-wrap items-center gap-2">
        {injectedBulkActions.map((action) => {
          const label = t(action.label, action.label)
          const iconNode = resolveInjectedIcon(action.icon, 'h-4 w-4 shrink-0')
          return (
            <Button
              key={action.id}
              type="button"
              size="sm"
              variant="outline"
              title={label}
              aria-label={label}
              className={iconNode ? 'px-2 sm:px-3' : undefined}
              disabled={action.requiresSelection !== false && selectedRows.length === 0}
              onClick={() => void runBulkAction(action)}
            >
              {iconNode}
              <span className={iconNode ? 'hidden sm:inline' : undefined}>{label}</span>
            </Button>
          )
        })}
      </div>
    ) : null
    return (
      <FilterBar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        searchAlign={searchAlign}
        filters={combined}
        values={filterValues}
        onApply={onFiltersApply}
        onClear={onFiltersClear}
        leadingItems={leadingItems}
        trailingItems={trailingItems}
        filtersExtraContent={fieldsetSelector}
        layout={embedded ? 'inline' : 'stacked'}
        className={embedded ? 'min-h-[2.25rem]' : undefined}
      />
    )
  }, [
    toolbar,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    searchAlign,
    baseFilters,
    cfFilters,
    injectedFilters,
    filterValues,
    onFiltersApply,
    onFiltersClear,
    canUsePerspectives,
    embedded,
    supportsCustomFieldFilterFieldsets,
    resolvedEntityIds,
    activeCustomFieldFilterFieldset,
    handleCustomFieldFilterFieldsetChange,
    cfFilterFieldsetsByEntity,
    hasInjectedBulkActions,
    injectedBulkActions,
    selectedRows.length,
    runBulkAction,
  ])

  const hasTitle = title != null
  const hasActions = actions !== undefined && actions !== null && actions !== false
  const shouldReserveActionsSpace = actions === null || actions === false
  const exportConfig = exporter === false ? null : exporter || null
  const resolvedExportSections = React.useMemo(() => resolveExportSections(exportConfig), [exportConfig])
  const hasExport = resolvedExportSections.length > 0
  const refreshButtonConfig = refreshButton
  const hasRefreshButton = Boolean(refreshButtonConfig)
  const hasToolbar = builtToolbar != null
  const shouldRenderActionsWrapper = hasActions || hasRefreshButton || shouldReserveActionsSpace || hasExport
  const renderToolbarInline = embedded && hasToolbar
  const shouldRenderToolbarBelow = hasToolbar && !renderToolbarInline
  const shouldRenderHeader = hasTitle || renderToolbarInline || shouldRenderActionsWrapper || shouldRenderToolbarBelow
  const containerClassName = embedded ? '' : 'rounded-lg border bg-card'
  const headerWrapperClassName = embedded ? 'pb-3' : 'px-4 py-3 border-b'
  const headerContentClassName = 'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'
  const toolbarWrapperClassName = embedded ? 'mt-2' : 'mt-3 pt-3 border-t'
  const tableScrollWrapperClassName = embedded ? '' : 'overflow-auto'

  const titleContent = hasTitle ? (
    <div className="text-base font-semibold leading-tight min-h-[2.25rem] flex items-center">
      {typeof title === 'string' ? <h2 className="text-base font-semibold">{title}</h2> : title}
    </div>
  ) : <div className="min-h-[2.25rem]" />

  return (
    <TooltipProvider delayDuration={300}>
    <div ref={containerRef} className={containerClassName} data-component-handle={resolvedReplacementHandle}>
      {shouldRenderHeader && (
        <div className={headerWrapperClassName}>
          {(hasTitle || shouldRenderActionsWrapper || renderToolbarInline) && (
            <div className={headerContentClassName}>
              <div className="flex-1 min-w-0">
                {renderToolbarInline ? builtToolbar : titleContent}
              </div>
              {shouldRenderActionsWrapper ? (
                <div className="flex flex-wrap items-center gap-2 min-h-[2.25rem]">
                  {refreshButtonConfig ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={refreshButtonConfig.onRefresh}
                      aria-label={refreshButtonConfig.label}
                      title={refreshButtonConfig.label}
                      disabled={refreshButtonConfig.disabled || refreshButtonConfig.isRefreshing}
                    >
                      {refreshButtonConfig.isRefreshing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      <span className="sr-only">{refreshButtonConfig.label}</span>
                    </Button>
                  ) : null}
                  {canUsePerspectives ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setPerspectiveOpen(true)}
                      aria-label={t('ui.dataTable.customizeColumns.ariaLabel', 'Customize columns')}
                      title={t('ui.dataTable.customizeColumns.title', 'Customize columns')}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">{t('ui.dataTable.customizeColumns.srOnly', 'Customize columns')}</span>
                    </Button>
                  ) : null}
                  {exportConfig && hasExport ? <ExportMenu config={exportConfig} sections={resolvedExportSections} /> : null}
                  {hasActions ? actions : null}
                </div>
              ) : null}
            </div>
          )}
          {shouldRenderToolbarBelow ? <div className={toolbarWrapperClassName}>{builtToolbar}</div> : null}
          {headerInjectionSpotId ? (
            <div className={embedded ? 'mt-2' : 'mt-3'}>
              <InjectionSpot spotId={headerInjectionSpotId} context={resolvedInjectionContext} />
            </div>
          ) : null}
        </div>
      )}
      <div className={tableScrollWrapperClassName}>
        <Table className="min-w-[640px] md:min-w-0">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hasInjectedBulkActions ? (
                  <TableHead className="w-8">
                    <Checkbox
                      checked={table.getIsAllPageRowsSelected()}
                      onCheckedChange={(checked) => {
                        table.toggleAllPageRowsSelected(Boolean(checked))
                      }}
                      aria-label={t('ui.dataTable.bulkAction.selectAll', 'Select all rows')}
                    />
                  </TableHead>
                ) : null}
                {hg.headers.map((header) => {
                  const columnMeta = (header.column.columnDef as any)?.meta
                  const priority = resolvePriority(header.column)
                  return (
                    <TableHead key={header.id} className={responsiveClass(priority, columnMeta?.hidden)}>
                      {header.isPlaceholder ? null : (
                        <Button
                          variant="ghost"
                          className={`h-auto p-0 font-medium ${sortable && header.column.getCanSort?.() ? 'cursor-pointer select-none' : ''}`}
                          onClick={() => sortable && header.column.toggleSorting?.(header.column.getIsSorted() === 'asc')}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortable && header.column.getIsSorted?.() ? (
                            <span className="text-xs text-muted-foreground">{header.column.getIsSorted() === 'asc' ? '▲' : '▼'}</span>
                          ) : null}
                        </Button>
                      )}
                    </TableHead>
                  )
                })}
                {rowActions || injectedRowActions.length > 0 ? (
                  <TableHead className="w-0 text-right">
                    {t('ui.dataTable.actionsColumn', 'Actions')}
                  </TableHead>
                ) : null}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={mergedColumns.length + (rowActions || injectedRowActions.length > 0 ? 1 : 0) + (hasInjectedBulkActions ? 1 : 0)} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Spinner size="md" />
                    <span className="text-muted-foreground">{t('ui.dataTable.loading', 'Loading data...')}</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={mergedColumns.length + (rowActions || injectedRowActions.length > 0 ? 1 : 0) + (hasInjectedBulkActions ? 1 : 0)} className="h-24 text-center text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => {
                const rowActionsElement = resolvedRowActions(row.original as T)
                const defaultRowAction = onRowClick ? null : pickDefaultRowAction(rowActionsElement, resolvedRowClickActionIds)
                const isClickable = !disableRowClick && (onRowClick || defaultRowAction)
                
                return (
                  <TableRow 
                    key={row.id} 
                    data-state={row.getIsSelected() && 'selected'}
                    className={isClickable ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}
                    onClick={isClickable ? (e) => {
                      // Don't trigger row click if clicking on actions cell
                      if ((e.target as HTMLElement).closest('[data-actions-cell]')) {
                        return
                      }
                      
                      if (onRowClick) {
                        onRowClick(row.original as T)
                      } else if (defaultRowAction) {
                        if (defaultRowAction.href) {
                          router.push(defaultRowAction.href)
                        } else if (defaultRowAction.onSelect) {
                          defaultRowAction.onSelect()
                        }
                      }
                    } : undefined}
                  >
                    {hasInjectedBulkActions ? (
                      <TableCell className="w-8">
                        <Checkbox
                          checked={row.getIsSelected()}
                          onCheckedChange={(checked) => row.toggleSelected(Boolean(checked))}
                          aria-label={t('ui.dataTable.bulkAction.selectRow', 'Select row')}
                          onClick={(event) => event.stopPropagation()}
                        />
                      </TableCell>
                    ) : null}
                    {row.getVisibleCells().map((cell) => {
                      const columnMeta = (cell.column.columnDef as any)?.meta
                      const priority = resolvePriority(cell.column)
                      const hasCustomCell = Boolean(cell.column.columnDef.cell)
                      const columnId = String((cell.column as any).id || '')
                      const accessorKey = String((cell.column.columnDef as any)?.accessorKey || '')
                      const isDateCol = dateColumnIds ? dateColumnIds.has(columnId) : false

                      let content: React.ReactNode
                      if (isDateCol) {
                        const raw = cell.getValue() as any
                        const d = tryParseDate(raw)
                        content = d ? simpleFormat(d, DATE_FORMAT) : (raw as any)
                      } else {
                        content = flexRender(cell.column.columnDef.cell, cell.getContext())
                      }

                      // Get truncation configuration for this column
                      const skipTruncation = shouldSkipTruncation(columnId)
                      // Get truncation configuration for this column
                      const truncateConfig = getColumnTruncateConfig(columnId, accessorKey, columnMeta)
                      const shouldTruncate = truncateConfig.truncate && !skipTruncation
                      const maxWidth = truncateConfig.maxWidth

                      // Wrap content with TruncatedCell if truncation is enabled
                      // Get raw cell value for tooltip - flexRender returns React elements
                      // that cannot have their text extracted, so we pass the raw value directly
                      // Check for custom tooltip content function in column meta for complex cells
                      const cellValue = cell.getValue()
                      const metaTooltipContent = columnMeta?.tooltipContent as ((row: unknown) => string | undefined) | undefined
                      const tooltipText = metaTooltipContent
                        ? metaTooltipContent(row.original)
                        : (cellValue != null ? String(cellValue) : undefined)

                      const wrappedContent = shouldTruncate ? (
                        <TruncatedCell maxWidth={maxWidth} tooltipContent={tooltipText}>
                          {content}
                        </TruncatedCell>
                      ) : content

                      return (
                        <TableCell key={cell.id} className={responsiveClass(priority, columnMeta?.hidden)}>
                          {wrappedContent}
                        </TableCell>
                      )
                    })}
                    {rowActions || injectedRowActions.length > 0 ? (
                      <TableCell className="text-right whitespace-nowrap" data-actions-cell>
                        {rowActionsElement}
                      </TableCell>
                    ) : null}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={mergedColumns.length + (rowActions || injectedRowActions.length > 0 ? 1 : 0) + (hasInjectedBulkActions ? 1 : 0)} className="h-24 text-center text-muted-foreground">
                  {emptyState ?? t('ui.dataTable.emptyState.default', 'No results.')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {footerInjectionSpotId ? (
        <div className={embedded ? 'mt-3' : 'px-4 py-3 border-t'}>
          <InjectionSpot spotId={footerInjectionSpotId} context={resolvedInjectionContext} />
        </div>
      ) : null}
      {paginationNode}
      {ConfirmDialogElement}
      {canUsePerspectives ? (
        <PerspectiveSidebar
          open={isPerspectiveOpen}
          onOpenChange={setPerspectiveOpen}
          loading={perspectiveQuery.isFetching && !perspectiveQuery.data}
          perspectives={perspectiveData?.perspectives ?? []}
          rolePerspectives={perspectiveData?.rolePerspectives ?? []}
          roles={perspectiveData?.roles ?? []}
          activePerspectiveId={activePerspectiveId}
          onActivatePerspective={handlePerspectiveActivate}
          onDeletePerspective={handlePerspectiveDelete}
          onClearRole={handleClearRole}
          onSave={handlePerspectiveSave}
          canApplyToRoles={Boolean(perspectiveData?.canApplyToRoles && canUseRoleDefaultsFeature)}
          columnOptions={columnOptions}
          onToggleColumn={handleToggleColumn}
          onMoveColumn={handleMoveColumn}
          saving={savePerspectiveMutation.isPending}
          deletingIds={deletingIds}
          roleClearingIds={roleClearingIds}
          apiWarning={perspectiveApiWarning}
        />
      ) : null}
    </div>
    </TooltipProvider>
  )
}
